import cx from 'classnames';
import Card from '../../../components/Card';
import { useUserSession } from '../../../components/UserSession';
import Page from '../../../layouts/DashboardLayout/Page';
import styles from './Analytics.module.scss';
import Typography from '../../../components/Typography';
import {
  ComponentPropsWithoutRef,
  ElementType,
  ReactNode,
  SVGProps,
} from 'react';
import Rating from '../../../components/Rating';
import {
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Area,
  AreaChart,
} from 'recharts';
import { sumBy } from 'lodash';
import { RiStarFill } from 'react-icons/ri';
import {
  useReportControllerGetWeeklyReport,
  useReportControllerGetWeeklyReportHistory,
} from '../../../api/informApi/informApiComponents';
import { SerializedReport } from '../../../api/informApi/informApiSchemas';
import { LabelList } from 'recharts';
import { Colors } from '../../../styles';

const summaryConfig: {
  label: string;
  description: string;
  color: Colors;
}[] = [
  {
    label: 'Poor',
    description: 'Your store experience needs work.',
    color: 'error',
  },
  {
    label: 'Ok',
    description: 'There are areas for improvement.',
    color: 'warning',
  },
  {
    label: 'Good',
    description: 'Your customers are satisfied!',
    color: 'success',
  },
  {
    label: 'Excellent',
    description: 'Great job serving your customers!',
    color: 'green',
  },
];

interface AnalyticsSectionProps {
  data?: SerializedReport;
}

type SummaryItemProps<T extends ElementType> = ComponentPropsWithoutRef<T> & {
  heading: ReactNode;
  content: ReactNode;
  footing: ReactNode;
  as?: T;
};
function SummaryItem<T extends ElementType>({
  heading,
  content,
  footing,
  as,
  ...props
}: SummaryItemProps<T>) {
  const Component = as ?? 'div';
  return (
    <Component {...props} className={cx(styles.summaryItem, props.className)}>
      <Typography
        className={styles.summaryHeading}
        as="p"
        textWeight="bold"
        textType="p3"
      >
        {heading}
      </Typography>
      <div className={styles.summaryContent}>{content}</div>
      <Typography as="p" textColor="text-2" textWeight="bold" textType="p5">
        {footing}
      </Typography>
    </Component>
  );
}

function Summary({ data }: AnalyticsSectionProps) {
  if (!data) return null;

  const startDate = new Date(data.startDate);
  const endDate = new Date(data.endDate);
  const dateFormat = new Intl.DateTimeFormat('en-CA', {
    month: 'short',
    day: 'numeric',
  });

  const grade = Math.floor((data.respondedReviews / data.totalReviews) * 3);
  const { label, description, color } = summaryConfig[grade];

  return (
    <div>
      <Typography textType="h4" as="h2">
        Summary for {dateFormat.format(startDate)} -{' '}
        {dateFormat.format(endDate)}
      </Typography>
      <div className={styles.summaryItems}>
        <SummaryItem
          heading="Your reviews for this week"
          footing={description}
          content={
            <Typography textColor={color} textType="h2">
              {label}
            </Typography>
          }
        />
        <div className={styles.summaryDivider} />
        <SummaryItem
          heading="You received"
          footing="This week"
          content={
            <div className={styles.summaryWeek}>
              <Typography className={styles.summaryStat} as="p">
                <Typography className={styles.summaryStatNumber} textType="h2">
                  {data.totalReviews}
                </Typography>
                <Typography textType="p1" textWeight="bold">
                  reviews
                </Typography>
              </Typography>
              <div className={styles.summaryTokens}>
                <Typography
                  className={styles.summaryToken}
                  textType="p3"
                  textColor="success"
                  textWeight="bold"
                >
                  {data.respondedReviews} responded
                </Typography>
                <Typography
                  className={styles.summaryToken}
                  textType="p3"
                  textColor="error"
                  textWeight="bold"
                >
                  {data.totalReviews - data.respondedReviews} not responded
                </Typography>
              </div>
            </div>
          }
        />
        <div className={styles.summaryDivider} />
        <SummaryItem
          heading="This week avg. rating"
          footing="Avg. rating on new reviews"
          content={
            <div className={styles.summaryStat}>
              <Typography textType="h2">
                {(data.averageRating / 10).toFixed(1)}
              </Typography>
              <Rating rating={Math.floor(data.averageRating / 10)} />
            </div>
          }
        />
      </div>
    </div>
  );
}

interface AnalysisItemProps extends ComponentPropsWithoutRef<'div'> {
  ratios: number[];
  notes: string[];
  label: string;
}
function AnalysisItem({ ratios, notes, label, ...props }: AnalysisItemProps) {
  const hasNotes = notes.length;
  const total = ratios[0] + ratios[1] + ratios[2];
  const data: Record<string, number> = {
    positive: ratios[0],
    neutral: ratios[1],
    negative: ratios[2],
  };

  const configByType: Record<string, { label: string; color: string }> = {
    positive: {
      label: 'Positive',
      color: 'success',
    },
    neutral: {
      label: 'Neutral',
      color: 'warning',
    },
    negative: {
      label: 'Negative',
      color: 'error',
    },
  };

  return (
    <Card
      {...props}
      backgroundColor="background-2"
      padding="1.25rem 1.25rem 1.5rem"
    >
      <Typography textWeight="bold" textType="p3">
        {label}
      </Typography>
      <ResponsiveContainer className={styles.analysisGraph} height={40}>
        <BarChart
          margin={{ top: 0, bottom: 20, left: 0, right: 0 }}
          data={[{ ...data, name: 'owo' }]}
          className={styles.chart}
          layout="vertical"
        >
          <XAxis type="number" domain={[0, total]} hide />
          <YAxis type="category" dataKey="name" hide />
          {Object.keys(data).map((dataKey) => {
            const config = configByType[dataKey];
            const value = data[dataKey];
            return (
              <Bar
                dataKey={dataKey}
                stackId="owo"
                fill={`rgba(var(--${config.color}))`}
                key={dataKey}
              >
                <LabelList
                  position="insideBottomLeft"
                  formatter={() => {
                    if (!value) return '';
                    return `${((value / total) * 100).toFixed(0)}% ${
                      config.label
                    }`;
                  }}
                  dy={18}
                  dx={-4}
                />
              </Bar>
            );
          })}
        </BarChart>
      </ResponsiveContainer>
      <Typography textWeight="bold" textType="p3">
        Notes to pay attention to:
      </Typography>
      <Typography
        textColor={hasNotes ? 'text-1' : 'text-2'}
        className={styles.comments}
        as={hasNotes ? 'ul' : 'p'}
        textType="p3"
      >
        {hasNotes
          ? notes.map((note, key) => <li key={key}>{note}</li>)
          : 'No notes avaliable'}
      </Typography>
    </Card>
  );
}

function RatingTick(props: SVGProps<SVGTextElement>) {
  return (
    <g transform={`translate(${props.x},${props.y})`}>
      <RiStarFill size="1rem" x={-55} y={-10} fill="rgb(var(--background-3))" />
      <text x={0} y={0} dx={-34} dy={2} textAnchor="start">
        {(props as any).payload.value}
      </text>
    </g>
  );
}

function Analytics() {
  const userCtx = useUserSession();
  const { data: report, isLoading: isLoadingReport } =
    useReportControllerGetWeeklyReport({
      pathParams: {
        id: userCtx.currentOrganization!,
      },
    });
  const { data: history, isLoading: isLoadingHistory } =
    useReportControllerGetWeeklyReportHistory({
      pathParams: {
        id: userCtx.currentOrganization!,
      },
    });
  const formatDate = (dateLike: string) => {
    const formatter = new Intl.DateTimeFormat('en', {
      month: 'short',
      day: 'numeric',
    });
    return formatter.format(new Date(dateLike));
  };

  return (
    <Page
      heading="Weekly Summary"
      loading={isLoadingReport || isLoadingHistory}
      className={styles.container}
    >
      <Card className={styles.wide} backgroundColor="background-1">
        <Summary data={report} />
      </Card>
      <Card className={styles.item} backgroundColor="background-1">
        <Typography textType="h4" as="h2">
          Standout Comments
        </Typography>
        <Typography
          className={styles.comments}
          textWeight="bold"
          textType="p3"
          as="ul"
        >
          {report?.standoutComments.map((comment, i) => (
            <li key={i}>{comment}</li>
          ))}
        </Typography>
      </Card>
      <Card className={styles.item} backgroundColor="background-1">
        <Typography textType="h4" as="h2">
          This Week's Rating Distribution
        </Typography>
        <div className={styles.chartLabel}>
          <Typography textType="p2" textWeight="bold">
            Rating Distribution
          </Typography>
          <Typography textType="p4" textWeight="bold" textColor="text-2">
            Over past week
          </Typography>
        </div>
        <ResponsiveContainer height={180}>
          <BarChart
            margin={{ left: 10, right: -20, top: -5, bottom: 0 }}
            className={styles.chart}
            layout="vertical"
            data={report?.ratingDistribution}
          >
            <YAxis
              type="category"
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              tick={<RatingTick />}
            />
            <YAxis
              type="category"
              yAxisId="e"
              orientation="right"
              dataKey="value"
              tickMargin={8}
              axisLine={false}
              tickLine={false}
            />
            <XAxis
              domain={[0, sumBy(report?.ratingDistribution, 'value')]}
              type="number"
              hide
            />
            <Bar
              dataKey="value"
              fill="rgb(var(--primary-1))"
              radius={999}
              background={{ fill: 'rgb(var(--background-3))', radius: 999 }}
              barSize={6}
            />
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <Card
        className={cx(styles.wide, styles.tall)}
        backgroundColor="background-1"
      >
        <Typography textType="h4" as="h2">
          Analytics
        </Typography>
        <div className={styles.analysisItems}>
          {report?.analysis.map((item, key) => (
            <AnalysisItem
              label={item.label}
              ratios={item.sentiments}
              notes={item.comments}
              key={key}
            />
          ))}
        </div>
      </Card>
      <Card className={styles.item} backgroundColor="background-1">
        <Typography textType="h4" as="h2">
          Historical Avg. Rating
        </Typography>
        <div className={styles.chartLabel}>
          <Typography textType="p2" textWeight="bold">
            Rating Average
          </Typography>
          <Typography textType="p4" textWeight="bold" textColor="text-2">
            Over past 4 weeks
          </Typography>
        </div>
        <ResponsiveContainer height={200}>
          <AreaChart
            margin={{ left: 2, right: 20, top: 8, bottom: 0 }}
            className={styles.chart}
            data={history?.map((item) => ({
              label: formatDate(item.label),
              value: item.value / 10,
            }))}
          >
            <defs>
              <linearGradient id="owo" x1="0" y1="0" x2="0" y2="4">
                <stop stopColor="rgb(var(--primary-1))" stopOpacity="0.15" />
                <stop
                  offset="25%"
                  stopColor="rgb(var(--primary-1))"
                  stopOpacity="0"
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              verticalCoordinatesGenerator={() => []}
              stroke="rgba(var(--primary-1), 0.5)"
              strokeDasharray="5 5"
              strokeWidth={2}
            />
            <XAxis
              tickMargin={10}
              axisLine={false}
              tickLine={false}
              dataKey="label"
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: number) => v.toPrecision(2)}
              tickMargin={5}
              width={30}
              domain={([l]) => [Math.floor(l), 5]}
            />
            <Tooltip cursor={{ strokeWidth: 2 }} />
            <Area
              type="monotone"
              dataKey="value"
              stroke="rgb(var(--primary-1))"
              activeDot={{ r: 6 }}
              dot={false}
              strokeWidth="3"
              fillOpacity="1"
              fill="url(#owo)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Card>
    </Page>
  );
}

Analytics.routeProps = {
  path: '/analytics',
};
export default Analytics;
